import type { CertificateStatus, CertificateVerdict } from '@geoic/shared';
import { api, type ListResult } from '@/lib/api-client.js';

export type CertificateRecord = {
  _id: string;
  number: string;
  fullName: string;
  photoMediaId: string;
  courseTitle: string;
  hours: number;
  studyStart: string;
  studyEnd: string;
  issuedAt: string;
  expiresAt: string;
  extendedUntil: string | null;
  status: CertificateStatus;
  revokedAt: string | null;
  revokeReason: string | null;
  /** QR ichidagi manzil — «Havolani nusxalash» shuni oladi. */
  verifyCode: string;
  verifyUrl: string;
  pdfMediaId: string | null;
  pdfGeneratedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type CertificateListParams = {
  page?: number;
  limit?: number;
  status?: CertificateStatus;
  search?: string;
};

export type CertificateFormValues = {
  number: string;
  fullName: string;
  photoMediaId: string;
  courseTitle: string;
  hours: number;
  studyStart: string;
  studyEnd: string;
  issuedAt: string;
};

export const certificatesApi = {
  list: (params: CertificateListParams, signal?: AbortSignal) =>
    api.get<ListResult<CertificateRecord>>('/certificates', { ...params }, signal),

  one: (id: string) =>
    api.get<{ data: CertificateRecord }>(`/certificates/${id}`).then((r) => r.data),

  create: (body: CertificateFormValues) =>
    api.post<{ data: CertificateRecord }>('/certificates', body).then((r) => r.data),

  update: (id: string, body: Partial<CertificateFormValues>) =>
    api.patch<{ data: CertificateRecord }>(`/certificates/${id}`, body).then((r) => r.data),

  regenerate: (id: string) =>
    api.post<{ data: CertificateRecord }>(`/certificates/${id}/generate`).then((r) => r.data),

  revoke: (id: string, reason: string | null) =>
    api
      .post<{ data: CertificateRecord }>(`/certificates/${id}/revoke`, { reason })
      .then((r) => r.data),

  extend: (id: string, extendedUntil?: string) =>
    api
      .post<{ data: CertificateRecord }>(
        `/certificates/${id}/extend`,
        extendedUntil ? { extendedUntil } : {},
      )
      .then((r) => r.data),

  downloadPdf: (id: string) => api.download(`/certificates/${id}/pdf`),
};

export const CERTIFICATE_STATUS_LABELS: Record<CertificateStatus, string> = {
  active: 'Amalda',
  revoked: 'Bekor qilingan',
};

/**
 * Ro'yxatda ko'rsatiladigan holat.
 *
 * «Muddati tugagan» bazada ALOHIDA holat emas — u sanadan hisoblanadi
 * (`packages/shared` dagi `certificateVerdictOf` bilan bir xil mantiq).
 * Shu sababli filtr faqat `active`/`revoked` bo'yicha ishlaydi.
 */
export function verdictOf(record: CertificateRecord): CertificateVerdict {
  if (record.status === 'revoked') return 'revoked';
  const validUntil = new Date(record.extendedUntil ?? record.expiresAt);
  return validUntil.getTime() < Date.now() ? 'expired' : 'valid';
}

export const VERDICT_LABELS: Record<CertificateVerdict, string> = {
  valid: 'Haqiqiy',
  expired: 'Muddati tugagan',
  revoked: 'Bekor qilingan',
};
